import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, HeartHandshake } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function Engagement() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    skills: "",
    availability: "",
    motivation: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.motivation.length < 10) {
      toast.error("Merci de préciser votre motivation (au moins 10 caractères).");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/volunteer`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        skills: form.skills || null,
        availability: form.availability || null,
        motivation: form.motivation,
      });
      toast.success("Merci ! Votre candidature nous est parvenue.");
      setForm({
        name: "",
        email: "",
        phone: "",
        skills: "",
        availability: "",
        motivation: "",
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Une erreur est survenue. Merci de réessayer."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="engagement-section"
      className="bg-[#F2EBE1] py-24 lg:py-36"
    >
      <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <p className="sdm-divider">S&apos;engager</p>
          <h2 className="font-serif-display text-4xl lg:text-5xl text-[#4A192C] mt-8 leading-tight">
            Devenir bénévole,
            <br />
            <em className="text-[#C5A059]">devenir sœur.</em>
          </h2>
          <p className="mt-6 text-[#2C1E23]/85 leading-relaxed text-lg">
            Juristes, artistes, thérapeutes, cuisinières, jardinières,
            entrepreneuses… chaque talent est une offrande précieuse pour le
            Sanctuaire. Rejoignez le cercle.
          </p>

          <div className="mt-10 p-6 bg-[#FAF7F2] border-l-4 border-[#C5A059]">
            <HeartHandshake className="w-8 h-8 text-[#C5A059]" />
            <p className="mt-4 font-serif-display italic text-lg text-[#4A192C]">
              « Seules nous allons vite, ensemble nous allons loin. »
            </p>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="volunteer-form"
          className="lg:col-span-7 space-y-6"
        >
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="sdm-label" htmlFor="vol-name">Nom complet *</label>
              <input
                id="vol-name"
                data-testid="volunteer-name-input"
                className="sdm-input"
                value={form.name}
                onChange={update("name")}
                required
                minLength={2}
              />
            </div>
            <div>
              <label className="sdm-label" htmlFor="vol-email">Email *</label>
              <input
                id="vol-email"
                data-testid="volunteer-email-input"
                type="email"
                className="sdm-input"
                value={form.email}
                onChange={update("email")}
                required
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="sdm-label" htmlFor="vol-phone">Téléphone</label>
              <input
                id="vol-phone"
                data-testid="volunteer-phone-input"
                className="sdm-input"
                value={form.phone}
                onChange={update("phone")}
              />
            </div>
            <div>
              <label className="sdm-label" htmlFor="vol-availability">Disponibilités</label>
              <input
                id="vol-availability"
                data-testid="volunteer-availability-input"
                className="sdm-input"
                placeholder="Ex : mercredis soirs, samedis matin"
                value={form.availability}
                onChange={update("availability")}
              />
            </div>
          </div>

          <div>
            <label className="sdm-label" htmlFor="vol-skills">Compétences / talents</label>
            <input
              id="vol-skills"
              data-testid="volunteer-skills-input"
              className="sdm-input"
              placeholder="Ex : droit, cuisine, thérapie, couture…"
              value={form.skills}
              onChange={update("skills")}
            />
          </div>

          <div>
            <label className="sdm-label" htmlFor="vol-motivation">
              Votre motivation *
            </label>
            <textarea
              id="vol-motivation"
              data-testid="volunteer-motivation-input"
              className="sdm-input resize-none"
              rows={5}
              value={form.motivation}
              onChange={update("motivation")}
              required
              minLength={10}
              placeholder="Dites-nous pourquoi vous souhaitez rejoindre le Sanctuaire…"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="volunteer-submit-button"
            className="sdm-btn-primary disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Envoi...
              </>
            ) : (
              <>Rejoindre le cercle</>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}
